import React from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, RefreshCw, Mail } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useCampaigns } from '../hooks/useCampaigns'
import { useCreateCampaignWithZeph } from '../hooks/useCreateCampaignWithZeph'
import CreateCampaignActions from './CreateCampaignActions'
import type { Campaign, CampaignStatus } from '../types'

type Props = {
  workspaceId: string
  onCreate: () => void
}

const STATUS_LABELS: Record<CampaignStatus, string> = {
  pending_approval: 'Pending approval',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  rejected: 'Rejected',
}

const StatusBadge: React.FC<{ status: CampaignStatus }> = ({ status }) => {
  const { currentTheme: t } = useTheme()
  const colors: Record<CampaignStatus, { bg: string; text: string }> = {
    pending_approval: { bg: t.warningBackground, text: t.warningText },
    active: { bg: t.successBackground, text: t.successText },
    paused: { bg: t.hoverBackground, text: t.mutedText },
    completed: { bg: t.infoBackground, text: t.infoText },
    rejected: { bg: t.errorBackground, text: t.errorText },
  }
  const { bg, text } = colors[status]
  return (
    <span
      className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
      style={{ backgroundColor: bg, color: text }}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

function recipientCount(campaign: Campaign): number {
  return campaign.recipients.length
}

const CampaignListSection: React.FC<Props> = ({ workspaceId, onCreate }) => {
  const { currentTheme: t } = useTheme()
  const navigate = useNavigate()
  const { campaigns, loading, error, retry } = useCampaigns(workspaceId)
  const createWithZeph = useCreateCampaignWithZeph()

  return (
    <div className="flex flex-col gap-5">
      {!loading && !error && campaigns.length > 0 && (
        <div className="flex items-center justify-end gap-3 flex-wrap">
          <CreateCampaignActions onCreateWithZeph={createWithZeph} onCreateManually={onCreate} />
        </div>
      )}

      {loading && (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.errorBackground }}>
            <AlertTriangle size={24} style={{ color: t.errorText }} />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load campaigns</p>
            <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error}</p>
          </div>
          <button
            type="button"
            onClick={retry}
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium"
            style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && campaigns.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.hoverBackground }}>
            <Mail size={24} style={{ color: t.mutedText }} />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: t.text }}>No campaigns yet</p>
            <p className="text-sm mt-1" style={{ color: t.mutedText }}>Create one to start an outreach sequence with automatic follow-ups.</p>
          </div>
          <div className="mt-1 flex justify-center">
            <CreateCampaignActions onCreateWithZeph={createWithZeph} onCreateManually={onCreate} />
          </div>
        </div>
      )}

      {!loading && !error && campaigns.length > 0 && (
        <div className="flex flex-col gap-2">
          {campaigns.map((campaign) => (
            <button
              key={campaign.id}
              type="button"
              onClick={() => navigate(`/dashboard/campaigns/${campaign.id}`)}
              className="flex items-center justify-between gap-4 rounded-xl p-4 text-left transition-colors hover:opacity-90"
              style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold truncate" style={{ color: t.text }}>{campaign.name}</span>
                  <StatusBadge status={campaign.status} />
                </div>
                <p className="text-[12.5px] mt-0.5" style={{ color: t.mutedText }}>
                  {recipientCount(campaign)} recipient{recipientCount(campaign) === 1 ? '' : 's'} · {campaign.steps.length} email{campaign.steps.length === 1 ? '' : 's'}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default React.memo(CampaignListSection)
